import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SocketContext } from '../../SocketProvider';
import { useDispatch, useSelector } from 'react-redux';
import io from 'socket.io-client';
import { ProgressBar } from 'react-bootstrap';
import { setStartSyncR, setStopSyncR } from '../../store/layoutSlice';
import { Button } from '@jobber/components/Button';
import { ConfirmationModal } from "@jobber/components/ConfirmationModal";
import { useMutation } from '@tanstack/react-query';
import { postToServer } from '../Common/requests';
import { setLoginUser } from '../../store/userSlice';
import { showToast } from '@jobber/components/Toast';
import { usePermission } from '../../hooks/use-permission';
import { ROUTE_PERMISSIONS, SYNC_PERMISSION } from '../../config/permissions';
import { useJobberSync } from '../../hooks/use-jobber-sync';

const dropdown = [
	{
		label: 'Sync Clients',
		link: '/clients',
		permissions: null,
	},
	{
		label: 'Sync Quotes',
		link: '/advance-quotes',
		permissions: ROUTE_PERMISSIONS.quotes,
	},
	{
		label: 'Sync Visits',
		link: '/advance-visits',
		permissions: ROUTE_PERMISSIONS.visits,
	},
	{
		label: 'Sync Jobs',
		link: '/job-automation?tab=closed-jobs',
		permissions: ROUTE_PERMISSIONS.jobAutomation,
	},
	{
		label: 'Sync Notes',
		link: '/advance-notes',
		permissions: ROUTE_PERMISSIONS.notes,
	}
];

function Sync() {
	const [show, setShow] = useState(false);
	const [modal, setModal] = useState(false);
	const { hasPermissions, allowed: canStartSync, isLoading: permissionLoading } = usePermission(SYNC_PERMISSION);
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const visibleDropdown = dropdown.filter((item) => !item.permissions || hasPermissions(item.permissions));

	const navigateHandler = (href) => {
		navigate(href, { state: { sync: true } });
		setShow(false);
	};

	const { socket, socketHandler } = useContext(SocketContext);
	const [loading, setLoading] = useState(false);
	const [comingMsg, setComingMsg] = useState(null);
	const [progressValue, setProgressValue] = useState(1);

	const userStore = useSelector((store) => store.user);
	const sync = useSelector((store) => store.layout);
	const user = userStore?.user?.data;
	const authFailureShownFor = useRef(null);
	const {
		job: durableSyncJob,
		isActive: hasActiveJob,
		progress: durableProgress,
		retryAt,
		authRequired,
	} = useJobberSync();

	useEffect(() => {
		if (hasActiveJob) {
			setLoading(true);
			setProgressValue(durableProgress);
		} else if (durableSyncJob && ['completed', 'failed', 'cancelled'].includes(durableSyncJob.status)) {
			setLoading(false);
			setProgressValue(durableSyncJob.status === 'completed' ? 100 : 0);
		}
	}, [durableSyncJob, durableProgress, hasActiveJob]);

	useEffect(() => {
		if (authRequired && durableSyncJob?.id && authFailureShownFor.current !== durableSyncJob.id) {
			authFailureShownFor.current = durableSyncJob.id;
			showToast({
				message: 'The Jobber connection needs to be renewed by an administrator.',
				variation: 'error',
			});
		}
	}, [authRequired, durableSyncJob]);

	useEffect(() => {
		if (sync.start && !loading) {
			setLoading(true);
			dispatch(setStartSyncR(false));
		}
	}, [sync.start]);

	useEffect(() => {
		if (comingMsg && !loading) {
			setLoading(true);
		}
		if (comingMsg) {
			setProgressValue(comingMsg?.progress);
		}
		if (comingMsg?.progress == '100' || comingMsg?.progress == 100) {
			setTimeout(() => {
				setProgressValue(1);
				setLoading(false);
			}, 1000);
		}
	}, [comingMsg]);

	useEffect(() => {
		if (!socket) {
			const newSocket = io(process.env.REACT_APP_API_URL);
			socketHandler(newSocket);
		}

		if (socket) {
			socket.on(`Genie_progress_${user?._id}`, (notify) => {
				setComingMsg(notify);
			});
			socket.on(`Genie_Quote_progress_${user?._id}`, (notify) => {
				setComingMsg(notify);
			});
			socket.on(`Genie_Visit_progress_${user?._id}`, (notify) => {
				setComingMsg(notify);
			});
			socket.on(`Genie_notes_progress_${user?._id}`, (notify) => {
				setComingMsg(notify);
			});
			socket.on(`Genie_Job_progress_${user?._id}`, (notify) => {
				console.log('socket called', notify);
				setComingMsg(notify);
			});

			socket.on(`Genie_Sync_End_${user?._id}`, (notify) => {
				setLoading(false);
				setProgressValue(1);
			});

			socket.on(`Genie_Quote_syncCompleted_${user?._id}`, (notify) => {
				setProgressValue(1);
				setLoading(false);
			});

			socket.on(`Genie_Quote_syncStarted_${user?._id}`, (notify) => {
				setLoading(true);
			});

			socket.on(`QuickBooksSyncStart_${user?._id}`, (msg) => {
				setLoading(true);
				setProgressValue(msg.progress);
			});

			socket.on(`QuickBooksSyncProgress_${user?._id}`, (msg) => {
				setLoading(true);
				setProgressValue(msg.progress);
			});

			socket.on(`QuickBooksSyncComplete_${user?._id}`, (msg) => {
				setLoading(false);
				setProgressValue(msg.progress);
			});

			socket.on(`QuickBooksSyncError_${user?._id}`, (msg) => {
				setLoading(false);
				setProgressValue(msg.progress);
			});
		}
	}, [socket]);

	const joinRoom = async () => {
		if (socket && user?._id) {
			await socket.emit('joinGenieRoom', { userId: user?._id });
		}
	};

	useEffect(() => {
		if (user?.isQuoteSyncing) {
			setLoading(true);
			setProgressValue(user?.progress);
		} else {
			setLoading(false);
			setProgressValue(1);
		}
	}, [user]);

	useEffect(() => {
		joinRoom();
	}, [user?._id, socket]);

	const { mutate: stopSync, isLoading: isStopLoading } = useMutation({
		mutationFn: () => postToServer('user/syncStop'),
		onSuccess: (res) => {
			console.log('stopSync res', res);
			if (res?.status == 200) {
				dispatch(setLoginUser({ ...userStore?.user, data: { ...userStore?.user?.data, isQuoteSyncing: false, progress: 0 } }));
				setLoading(false);
				setProgressValue(0);

				showToast({
					type: 'success',
					message: 'Sync Stopped',
				});
			}
		},
		onError: (err) => {
			setLoading(false);
			setProgressValue(0);
			showToast({
				type: 'error',
				message: err?.response?.data?.message || 'Something went wrong',
			});
		},
	})

	return (
		<>
			{!permissionLoading && !canStartSync ? null : (
				<>
					{loading ? (
						<>
							<div style={{ width: '30vw' }} className="mt-2">
								<ProgressBar animated variant="success" label={retryAt ? 'Waiting for Jobber' : `${Number(progressValue).toFixed(2)}%`} now={progressValue} />
							</div>
							<Button type='secondary' variation="destructive" icon='minus' onClick={() => setModal(true)} />
							<ConfirmationModal
								title={"Are you sure?"}
								message={"Only proceed if sync looks stuck. Otherwise, please wait for it to finish."}
								open={modal}
								confirmLabel="Yes"
								onConfirm={() => stopSync()}
								onCancel={() => setModal(false)}
								onRequestClose={() => setModal(false)}
							/>
						</>
					) : (
						<div className="position-relative">
							<Button label='Sync' onClick={() => setShow(!show)} />
							{show && (
								<div className="position-absolute" style={{ left: '-1.4rem' }}>
									<div className="card">
										<div className="d-flex flex-column gap-1 py-2 sync__links">
											{visibleDropdown?.map((d) => (
												<span className="ps-2 pe-2 text-nowrap" onClick={() => navigateHandler(d.link)}>
													{d.label}
												</span>
											))}
										</div>
									</div>
								</div>
							)}
						</div>
					)}
				</>
			)}
		</>
	);
}

export default Sync;
